import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Circle, Layer, Text } from 'react-konva';

export const SHARED_PING_LIFETIME_MS = 5000;
export const SHARED_PING_EXPANSION_MS = 1250;

const SharedPingLayer = ({ pings = [], now, origin = { x: 0, y: 0 } }) => {
  const hasControlledClock = Number.isFinite(Number(now));
  const prefersReducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [animationNow, setAnimationNow] = useState(() => Date.now());
  const effectiveNow = hasControlledClock ? Number(now) : animationNow;

  useEffect(() => {
    if (hasControlledClock || prefersReducedMotion || pings.length === 0) return undefined;
    const intervalId = window.setInterval(() => setAnimationNow(Date.now()), 50);
    return () => window.clearInterval(intervalId);
  }, [hasControlledClock, pings.length, prefersReducedMotion]);

  const originX = Number(origin?.x) || 0;
  const originY = Number(origin?.y) || 0;
  const activePings = pings.filter((ping) => (
    ping
    && Number.isFinite(Number(ping.x))
    && Number.isFinite(Number(ping.y))
    && Number(ping.expiresAt) > effectiveNow
  ));

  if (activePings.length === 0) return null;

  return (
    <Layer listening={false} name="shared-map-pings">
      {activePings.map((ping) => {
        const label = String(ping.ownerName || 'Player').slice(0, 60);
        const labelWidth = Math.max(76, Math.min(180, label.length * 6.5 + 18));
        const x = Number(ping.x) + originX;
        const y = Number(ping.y) + originY;
        const createdAt = Number.isFinite(Number(ping.createdAt))
          ? Number(ping.createdAt)
          : Number(ping.expiresAt) - SHARED_PING_LIFETIME_MS;
        const expansionProgress = prefersReducedMotion
          ? 1
          : Math.max(0, Math.min(1, (effectiveNow - createdAt) / SHARED_PING_EXPANSION_MS));
        const expandingRadius = 18 + (42 * expansionProgress);
        const expandingOpacity = Math.max(0, 0.9 * (1 - expansionProgress));
        const actionLabel = ping.focusCamera ? 'Focus' : 'Ping';
        return (
          <React.Fragment key={ping.pingId}>
            {expansionProgress < 1 && (
              <Circle
                x={x}
                y={y}
                radius={expandingRadius}
                fill="transparent"
                stroke="#89c8d4"
                strokeWidth={4}
                opacity={expandingOpacity}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}
            <Circle
              x={x}
              y={y}
              radius={18}
              fill="rgba(42, 97, 109, 0.16)"
              stroke="#89c8d4"
              strokeWidth={3}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Circle
              x={x}
              y={y}
              radius={5}
              fill="#d0b07a"
              stroke="#0f1623"
              strokeWidth={2}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Text
              x={x - labelWidth / 2}
              y={y - 38}
              width={labelWidth}
              height={18}
              text={`${actionLabel} · ${label}`}
              fontFamily="Inter, sans-serif"
              fontSize={11}
              fontStyle="bold"
              fill="#f3ead7"
              stroke="#0f1623"
              strokeWidth={0.8}
              align="center"
              listening={false}
              perfectDrawEnabled={false}
            />
          </React.Fragment>
        );
      })}
    </Layer>
  );
};

SharedPingLayer.propTypes = {
  pings: PropTypes.arrayOf(PropTypes.shape({
    pingId: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    ownerName: PropTypes.string,
    focusCamera: PropTypes.bool,
    createdAt: PropTypes.number,
    expiresAt: PropTypes.number.isRequired,
  })),
  now: PropTypes.number,
  origin: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
};

export default React.memo(SharedPingLayer);
